import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { CustomerDetailData, Announcement } from '~/types/customer-detail.types';
import type { DateRange } from '@sakura-grp/ui/calendar';
import { CancelActivitySheet } from '~/components/app-hostesses/announcements/cancel-activity-sheet';
import { EditActivityDateSheet } from '~/components/app-hostesses/announcements/edit-activity-date-sheet';
import { AnnouncementsFilterDialog } from '~/components/app-hostesses/announcements/filter-dialog';
import { StoreFilter } from '~/components/ui/store-filter';
import { useStoreFilterState } from '@/hooks/useStoreFilterState';
import { useImeSafeSearch } from '@/hooks/useImeSafeSearch';
import { ActivityRow, type ActivityRowData } from '~/components/app-hostesses/announcements/activity-row';
import { useResolvedCustomerCompany } from '@/hooks/useResolvedCustomerCompany';

interface AnnouncementsTabContentProps {
  data: CustomerDetailData;
}

export const AnnouncementsTabContent = ({ data }: AnnouncementsTabContentProps) => {
  // 仕様: docs/domain/20260527-customer-company-context-display.md §営業履歴
  // 閲覧者視点 (ログイン中の自分) で代表名刺の会社を解決して、全 activity 行で表示
  const resolvedCompanyName = useResolvedCustomerCompany(data.id);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Announcement | null>(null);
  const [editDateDialogOpen, setEditDateDialogOpen] = useState(false);
  const [editingDateActivity, setEditingDateActivity] = useState<Announcement | null>(null);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [appliedSearchText, setAppliedSearchText] = useState('');
  const search = useImeSafeSearch({onApply: setAppliedSearchText});
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const storeItems = useMemo(() =>
    data.stores.map((s, i) => ({ id: String(i), shortName: s.storeShortName })),
    [data.stores]
  );
  const { selectedStores, toggleStore, storeFilterItems } = useStoreFilterState({ stores: storeItems });

  const handleCancelClick = (activity: ActivityRowData) => {
    const announcement = data.announcements.find((a) => a.id === activity.id);
    if (announcement) {
      setSelectedActivity(announcement);
      setCancelDialogOpen(true);
    }
  };

  const handleEditDateClick = (activity: ActivityRowData) => {
    const announcement = data.announcements.find((a) => a.id === activity.id);
    if (announcement) {
      setEditingDateActivity(announcement);
      setEditDateDialogOpen(true);
    }
  };

  // フィルタリング
  // 顧客詳細画面では:
  //   トグルOFF（デフォルト）= この顧客の全ホステスの営業履歴を表示
  //   トグルON = 自分の営業履歴のみ表示
  const filteredAnnouncements = useMemo(() => {
    const query = appliedSearchText.trim().toLowerCase();
    return [...data.announcements].sort((a, b) => {
      // 日付降順（新しい順）
      return b.date.localeCompare(a.date);
    }).filter((a) => {
      if (showOnlyMine) {
        if (!a.isMyActivity) return false;
      }

      // テキスト検索（日付・ホステス名）
      if (query) {
        const matchDate = a.date?.toLowerCase().includes(query);
        const matchHostess = a.hostess?.name?.toLowerCase().includes(query);
        if (!matchDate && !matchHostess) return false;
      }

      // 日付範囲フィルタ
      if (dateRange?.from) {
        const activityDate = new Date(a.date.replace(/\//g, '-'));
        const from = new Date(dateRange.from);
        from.setHours(0, 0, 0, 0);
        if (activityDate < from) return false;
        if (dateRange.to) {
          const to = new Date(dateRange.to);
          to.setHours(23, 59, 59, 999);
          if (activityDate > to) return false;
        }
      }

      if (selectedStores.length > 0) {
        const storeMatch = data.stores.some(
          (s) =>
            selectedStores.includes(s.storeShortName) &&
            (a.store === s.storeName ||
              a.store === s.storeShortName ||
              s.storeName?.includes(a.store) ||
              a.store?.includes(s.storeShortName))
        );
        if (!storeMatch) return false;
      }

      return true;
    });
  }, [data.announcements, showOnlyMine, appliedSearchText, dateRange, selectedStores, data.stores]);

  return (
    <div className="border-t">
      {/* ツールバー */}
      <div className="px-lg gap-md h-(--h-list) flex items-center border-b">
        <label className="space-y-sm grid cursor-pointer place-items-center leading-none">
          <input
            type="checkbox"
            checked={showOnlyMine}
            onChange={(e) => setShowOnlyMine(e.target.checked)}
            className="peer sr-only"
          />
          <div className="peer relative h-6 w-11 rounded-full border-gray-500 bg-gray-400 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand"></div>
          <p className="text-2xs -mx-sm text-center font-bold tracking-tight text-stone-400 peer-checked:text-brand">
            自分のみ
          </p>
        </label>

        <div className="flex grow overflow-hidden rounded-lg border">
          <input
            data-slot="input"
            className="h-xl bg-input/10 text-md shadow-xs selection:bg-primary selection:text-primary-foreground file:text-foreground placeholder:text-muted-foreground/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex w-full min-w-[4rem] shrink px-3 py-1 outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            placeholder="報告日と名前"
            {...search.inputProps}
          />
          <button
            data-slot="popover-trigger"
            className="h-xl bg-background p-md shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 inline-flex items-center justify-start whitespace-nowrap border-s text-left text-xs font-normal tracking-tight outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50"
            type="button"
            onClick={() => setFilterOpen(true)}
          >
            {dateRange?.from ? '期間選択中' : '絞る'}
          </button>
        </div>

        <StoreFilter
          stores={storeFilterItems}
          selectedStores={selectedStores}
          onToggle={toggleStore}
        />
      </div>

      {/* 営業履歴リスト */}
      <ul className="divide-y divide-stone-200 border-b">
        {filteredAnnouncements.length > 0 ? (
          filteredAnnouncements.map((announcement) => (
            <ActivityRow
              key={announcement.id}
              activity={announcement}
              onActionClick={handleCancelClick}
              onEditDateClick={handleEditDateClick}
              customerCell={
                <Link to={`/customers/${data.id}`} className="space-y-sm">
                  <time className="block text-xs font-bold tracking-tight text-stone-500">
                    {announcement.date}
                  </time>
                  <div className="flex items-center">
                    <div className="float-none w-[2.45rem] text-center text-[2.45rem]">{data.icon}</div>
                    <div className="space-y-xs leading-none">
                      <h3 className="text-xl">{data.name}</h3>
                      <small className="mx-xs text-(length:--smaller-xl) line-clamp-1 tracking-normal">
                        {resolvedCompanyName && `( ${resolvedCompanyName} )`}
                      </small>
                    </div>
                  </div>
                </Link>
              }
            />
          ))
        ) : (
          <li className="px-lg py-xl text-center text-sm text-gray-400">営業履歴がありません</li>
        )}
      </ul>

      {/* 日付範囲フィルタダイアログ */}
      <AnnouncementsFilterDialog
        open={filterOpen}
        onOpenChange={setFilterOpen}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />

      {/* キャンセルシート */}
      <CancelActivitySheet
        activity={selectedActivity ? {
          id: selectedActivity.id,
          date: selectedActivity.date,
          type: selectedActivity.type,
          storeName: selectedActivity.store,
          customerName: data.name,
        } : null}
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        hostessId={selectedActivity?.hostess.id ?? ''}
      />

      {/* 時刻訂正シート */}
      <EditActivityDateSheet
        activity={editingDateActivity ? {
          id: editingDateActivity.id,
          activityDateIso: editingDateActivity.activityDateIso,
          type: editingDateActivity.type,
          storeName: editingDateActivity.store,
          customerName: data.name,
        } : null}
        open={editDateDialogOpen}
        onOpenChange={(open) => {
          setEditDateDialogOpen(open);
          if (!open) setEditingDateActivity(null);
        }}
      />
    </div>
  );
};
